class GlideSlider {
  /**
   * @param {string} elementId - The ID of the main carousel container.
   * @param {Object} options    - Configuration options.
   *   thumbnails:
   *     - true  => auto-generate a thumbs wrapper BELOW the carousel
   *     - false => no thumbnails
   *     - { containerId: 'yourId' } => use existing element
   */
  constructor(elementId, options = {}) {
    this.container = document.getElementById(elementId);
    if (!this.container) {
      console.error(`GlideSlider Error: Element with ID "${elementId}" not found.`);
      return;
    }

    // Defaults (slides array is ignored; we use DOM children)
    const defaults = {
      loop: false,
      autoplay: { delay: 3000, pauseOnHover: true },
      navigation: true,
      pagination: true,
      thumbnails: true, // <-- IMPORTANT: boolean by default (auto-generate)
      videoAutoplay: true // Enable video autoplay when slide becomes active
    };

    this.settings = { ...defaults, ...options };
    this.settings.autoplay = { ...defaults.autoplay, ...options.autoplay };
    // allow boolean or object {containerId}
    this.settings.thumbnails =
      typeof options.thumbnails === 'undefined' ? defaults.thumbnails : options.thumbnails;

    this.wrapper = this.container.querySelector('.glide-slider-wrapper');
    if (!this.wrapper) {
      console.error('GlideSlider Error: Missing .glide-slider-wrapper inside container.');
      return;
    }

    // State
    this.slideElements = [];
    this.totalSlides = 0;
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = null;
    this.thumbContainer = null;
    this.thumbnails = null;

    // Flag to prevent overlapping clone transitions
    this.isCloning = false;

    // Flag to avoid cloning while the page is being scrolled
    this.isPageScrolling = false;
    this._scrollTimeout = null;

    this.init();
  }

  init() {
    this.wrapExistingSlides();
    if (this.totalSlides === 0) {
      console.error('GlideSlider Error: No slides found inside .glide-slider-wrapper.');
      return;
    }

    this.createPagination();
    this.ensureThumbContainer();  // <-- ensure container exists (auto-create if needed)
    this.createThumbnails();      // <-- then build thumbs

    this.setupEventListeners();
    this.update();

    if (this.settings.autoplay.delay) this.startAutoplay();
  }

  /**
   * Wrap each existing child node into a `.glide-slide` element.
   */
  wrapExistingSlides() {
    const children = Array.from(this.wrapper.children);
    if (!children.length) return;

    this.wrapper.style.display = this.wrapper.style.display || 'flex';
    this.wrapper.style.willChange = this.wrapper.style.willChange || 'transform';
    this.wrapper.style.touchAction = this.wrapper.style.touchAction || 'pan-y';

    const frag = document.createDocumentFragment();

    children.forEach((child, idx) => {
      const slide = document.createElement('div');
      slide.className = 'glide-slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Slide ${idx + 1} of ${children.length}`);
      
      slide.appendChild(child);
      frag.appendChild(slide);
    });

    this.wrapper.innerHTML = '';
    this.wrapper.appendChild(frag);

    this.slideElements = Array.from(this.wrapper.children);
    this.totalSlides = this.slideElements.length;

    this.slideElements.forEach(slide => {
      if (!slide.style.flex) slide.style.flex = '0 0 100%';
    });

    // Setup videos after slides are created
    this.setupVideos();
  }

  /**
   * Setup video elements for proper autoplay behavior
   */
  setupVideos() {
    this.slideElements.forEach((slide, idx) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach(video => {
        // Ensure videos are muted for autoplay policies
        video.muted = true;
        video.playsInline = true;
        
        // Add load event to handle video ready state
        video.addEventListener('loadeddata', () => {
          if (idx === this.currentIndex && this.settings.videoAutoplay) {
            // Autoplay video if it's in the current slide
            this.playVideo(video);
          }
        });

        // Update thumbnail when video metadata loads (if no poster was set)
        video.addEventListener('loadedmetadata', () => {
          if (!video.poster && this.thumbnails && this.thumbnails[idx]) {
            const newThumbnail = this.generateVideoThumbnail(video);
            if (newThumbnail) {
              this.thumbnails[idx].src = newThumbnail;
            }
          }
        });
      });
    });
  }

  createPagination() {
    if (!this.settings.pagination) return;
    const paginationContainer = this.container.querySelector('.glide-slider-pagination');
    if (!paginationContainer) return;

    paginationContainer.innerHTML = '';
    for (let i = 0; i < this.totalSlides; i++) {
      const bullet = document.createElement('button');
      bullet.className = 'glide-slider-bullet';
      bullet.type = 'button';
      bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);
      bullet.addEventListener('click', () => this.goTo(i));
      paginationContainer.appendChild(bullet);
    }
    this.bullets = Array.from(paginationContainer.children);
  }

  /**
   * Ensure we have a thumbnail container:
   * - if thumbnails === true, auto-create a wrapper below the carousel
   * - if thumbnails is { containerId }, use that element if present
   */
  ensureThumbContainer() {
    if (!this.settings.thumbnails) return;

    if (this.thumbContainer) return;

    const thumbsOpt = this.settings.thumbnails;

    if (typeof thumbsOpt === 'object' && thumbsOpt.containerId) {
      const el = document.getElementById(thumbsOpt.containerId);
      if (el) {
        this.thumbContainer = el;
        return;
      }
    }

    if (thumbsOpt === true) {
      const thumbs = document.createElement('div');
      thumbs.className = 'glide-slider-thumbs';
      thumbs.setAttribute('role', 'tablist');
      thumbs.setAttribute('aria-label', 'Slide thumbnails');
      // place directly AFTER the carousel container
      this.container.insertAdjacentElement('afterend', thumbs);
      this.thumbContainer = thumbs;
    }
  }

  createThumbnails() {
    if (!this.settings.thumbnails) return;

    this.ensureThumbContainer();
    if (!this.thumbContainer) return;

    this.thumbContainer.innerHTML = '';

    this.slideElements.forEach((slide, index) => {
      let src = 'https://placehold.co/100x100/e2e8f0/4a5568?text=N%2FA';
      let alt = `Thumbnail for slide ${index + 1}`;
      let isVideo = false;

      // Check for video first (prioritize video poster)
      const video = slide.querySelector('video');
      if (video) {
        isVideo = true;
        if (video.poster) {
          src = video.poster;
          alt = `Video thumbnail for slide ${index + 1}`;
        } else {
          // If no poster, try to generate a thumbnail from video
          src = this.generateVideoThumbnail(video) || src;
          alt = `Video thumbnail for slide ${index + 1}`;
        }
      } else {
        // Check for external video (iframe)
        const iframe = slide.querySelector('iframe');
        if (iframe) {
          isVideo = true;
          src = this.generateExternalVideoThumbnail(iframe) || src;
          alt = `Video thumbnail for slide ${index + 1}`;
        } else {
          // Fall back to image
          const img = slide.querySelector('img');
          if (img) {
            src = img.dataset.thumbSrc || img.currentSrc || img.src || src;
            alt = img.alt || alt;
          }
        }
      }

      // Create thumbnail image
      const thumb = document.createElement('img');
      thumb.src = src;
      thumb.alt = alt;
      thumb.className = 'glide-slider-thumb';
      thumb.decoding = 'async';
      thumb.loading = 'lazy';

      // If it's a video, wrap it in a container for the play button overlay
      if (isVideo) {
        const wrapper = document.createElement('div');
        wrapper.className = 'glide-slider-thumb-wrapper video-thumb';
        wrapper.appendChild(thumb);
        wrapper.addEventListener('click', () => this.goTo(index));
        this.thumbContainer.appendChild(wrapper);
      } else {
        thumb.addEventListener('click', () => this.goTo(index));
        this.thumbContainer.appendChild(thumb);
      }
    });

    this.thumbnails = Array.from(this.thumbContainer.children);
  }

  /**
   * Generate a thumbnail from a video element
   */
  generateVideoThumbnail(video) {
    if (!video || !video.videoWidth || !video.videoHeight) return null;
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size to match video aspect ratio but smaller for thumbnail
      const aspectRatio = video.videoWidth / video.videoHeight;
      canvas.width = 100;
      canvas.height = 100 / aspectRatio;
      
      // Draw current video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert to data URL
      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.warn('Failed to generate video thumbnail:', error);
      return null;
    }
  }

  /**
   * Generate a thumbnail URL for external videos (YouTube, Vimeo)
   */
  generateExternalVideoThumbnail(iframe) {
    if (!iframe) return null;
    
    // Check if iframe has a data-poster attribute (from Shopify media)
    if (iframe.dataset && iframe.dataset.poster) {
      return iframe.dataset.poster;
    }
    
    // Fall back to extracting from src
    if (!iframe.src) return null;
    
    try {
      const src = iframe.src;
      
      // YouTube thumbnail
      if (src.includes('youtube.com/embed/')) {
        const videoId = src.split('/embed/')[1].split('?')[0];
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
      
      // Vimeo thumbnail (this requires an API call, so we'll use a placeholder for now)
      if (src.includes('player.vimeo.com/video/')) {
        const videoId = src.split('/video/')[1].split('?')[0];
        // Vimeo thumbnails require API access, using placeholder
        return `https://placehold.co/100x56/333/fff?text=Vimeo`;
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to generate external video thumbnail:', error);
      return null;
    }
  }

  setupEventListeners() {
    const nextBtn = this.container.querySelector('.next');
    const prevBtn = this.container.querySelector('.prev');
    if (nextBtn) nextBtn.addEventListener('click', () => this.next());
    if (prevBtn) prevBtn.addEventListener('click', () => this.prev());

    if (this.settings.autoplay.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
    }

    this.wrapper.addEventListener('mousedown', this.dragStart.bind(this));
    this.wrapper.addEventListener('touchstart', this.dragStart.bind(this), { passive: true });
    this.wrapper.addEventListener('mouseup', this.dragEnd.bind(this));
    this.wrapper.addEventListener('touchend', this.dragEnd.bind(this));
    this.wrapper.addEventListener('mouseleave', this.dragEnd.bind(this));
    this.wrapper.addEventListener('mousemove', this.drag.bind(this));
    this.wrapper.addEventListener('touchmove', this.drag.bind(this), { passive: true });

    // Prevent cloning while the user is scrolling the page — set a short debounce
    window.addEventListener('scroll', () => {
      this.isPageScrolling = true;
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = setTimeout(() => {
        this.isPageScrolling = false;
      }, 150);
    }, { passive: true });

    window.addEventListener('resize', () => this.updatePosition());
  }

  goTo(index) {
    if (index < 0) {
      // If loop enabled, perform a smooth backward loop by cloning the last slide
      if (this.settings.loop) {
        this.loopBackwardTransition();
        return;
      }
      this.currentIndex = 0;
    } else if (index >= this.totalSlides) {
      // If loop is enabled, perform a smooth forward loop by cloning the first slide
      if (this.settings.loop) {
        this.loopForwardTransition();
        return; // we'll update state after the clone transition completes
      } else {
        this.currentIndex = this.totalSlides - 1;
      }
    } else {
      this.currentIndex = index;
    }
    this.update();
  }

  next() { this.goTo(this.currentIndex + 1); }
  prev() { this.goTo(this.currentIndex - 1); }

  update() {
    this.updatePosition();
    this.updatePagination();
    this.updateThumbnailsActive();
    this.updateNavButtons();
    this.handleVideoPlayback();
  }

  updatePosition() {
    const width = this.container.offsetWidth || 0;
    const target = -this.currentIndex * width;
    this.prevTranslate = target;
    this.currentTranslate = target;
    this.wrapper.style.transition = 'transform 0.4s ease';
    this.wrapper.style.transform = `translateX(${target}px)`;
  }

  updatePagination() {
    if (!this.bullets) return;
    this.bullets.forEach((b, i) => b.classList.toggle('active', i === this.currentIndex));
  }

updateThumbnailsActive() {
    if (!this.thumbContainer) return;

    // Ensure there is an inner wrapper we can translate
    let inner = this.thumbContainer.querySelector('.glide-slider-thumbs-inner');
    if (!inner) {
        inner = document.createElement('div');
        inner.className = 'glide-slider-thumbs-inner';
        // move existing thumbs into the inner wrapper
        while (this.thumbContainer.firstChild) {
            inner.appendChild(this.thumbContainer.firstChild);
        }
        this.thumbContainer.appendChild(inner);

        // Ensure container hides overflow and inner is set up for transform animations
        this.thumbContainer.style.overflow = 'hidden';
        inner.style.display = 'flex';
        inner.style.willChange = 'transform';
        inner.style.transition = 'transform 300ms ease';
    }

    // Update internal thumbnails reference to the inner children
    this.thumbnails = Array.from(inner.children);
    if (!this.thumbnails.length) return;

    // Toggle active class (handle both wrapped and unwrapped thumbnails)
    this.thumbnails.forEach((t, i) => {
      const isActive = i === this.currentIndex;
      if (t.classList.contains('glide-slider-thumb-wrapper')) {
        // Wrapped thumbnail (video)
        t.classList.toggle('active', isActive);
        const img = t.querySelector('img');
        if (img) img.classList.toggle('active', isActive);
      } else {
        // Direct thumbnail (image)
        t.classList.toggle('active', isActive);
      }
    });

    const activeThumb = this.thumbnails[this.currentIndex];
    if (!activeThumb) return;

    const containerWidth = this.thumbContainer.offsetWidth;
    const innerWidth = inner.scrollWidth;

    // account for container paddings so centering is correct
    const style = getComputedStyle(this.thumbContainer);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const visibleWidth = Math.max(0, containerWidth - padLeft - padRight);

    // Only translate if the thumbs overflow the visible area
    if (innerWidth > visibleWidth) {
        const thumbOffsetLeft = activeThumb.offsetLeft; // relative to inner
        const thumbWidth = activeThumb.offsetWidth;

        // Calculate desired scroll position to center the active thumb within the visible area
        let desired = thumbOffsetLeft + thumbWidth / 2 - visibleWidth / 2;

        // Clamp to valid range based on visible width (accounting for paddings)
        desired = Math.max(0, Math.min(desired, innerWidth - visibleWidth));

        // Use translate3d for smooth GPU-accelerated animation
        const translateX = -desired;
        inner.style.transform = `translate3d(${translateX}px, 0, 0)`;
    } else {
        // Reset transform if no overflow
        inner.style.transform = '';
    }
}

  updateNavButtons() {
    if (this.settings.loop) return;
    const prevBtn = this.container.querySelector('.prev');
    const nextBtn = this.container.querySelector('.next');
    if (prevBtn) prevBtn.style.display = this.currentIndex === 0 ? 'none' : 'flex';
    if (nextBtn) nextBtn.style.display = this.currentIndex === this.totalSlides - 1 ? 'none' : 'flex';
  }

  /**
   * Handle video playback - play current slide videos, pause others
   */
  handleVideoPlayback() {
    if (!this.settings.videoAutoplay) return;

    this.slideElements.forEach((slide, index) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach(video => {
        if (index === this.currentIndex) {
          // Play video in current slide
          this.playVideo(video);
        } else {
          // Pause videos in other slides
          this.pauseVideo(video);
        }
      });
    });
  }

  /**
   * Play a video with error handling
   */
  playVideo(video) {
    if (!video) return;
    
    try {
      // Reset video to beginning if it has ended
      if (video.ended) {
        video.currentTime = 0;
      }
      
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Video autoplay failed:', error);
        });
      }
    } catch (error) {
      console.warn('Video play error:', error);
    }
  }

  /**
   * Pause a video with error handling
   */
  pauseVideo(video) {
    if (!video) return;
    
    try {
      video.pause();
    } catch (error) {
      console.warn('Video pause error:', error);
    }
  }

  /**
   * Pause all videos in the slider
   */
  pauseAllVideos() {
    this.slideElements.forEach(slide => {
      const videos = slide.querySelectorAll('video');
      videos.forEach(video => this.pauseVideo(video));
    });
  }

  startAutoplay() {
    if (!this.settings.autoplay.delay || this.autoplayInterval) return;
    this.autoplayInterval = setInterval(() => {
      // Check if current slide has playing video
      if (this.settings.videoAutoplay && this.hasPlayingVideo()) {
        return; // Skip autoplay if video is playing
      }
      this.next();
    }, this.settings.autoplay.delay);
  }

  stopAutoplay() {
    clearInterval(this.autoplayInterval);
    this.autoplayInterval = null;
  }

  /**
   * Check if current slide has a playing video
   */
  hasPlayingVideo() {
    if (this.currentIndex >= this.slideElements.length) return false;
    
    const currentSlide = this.slideElements[this.currentIndex];
    const videos = currentSlide.querySelectorAll('video');
    
    return Array.from(videos).some(video => 
      !video.paused && !video.ended && video.readyState > 2
    );
  }

  // Dragging
  dragStart(event) {
    this.isDragging = true;
    this.stopAutoplay();
    this.pauseAllVideos(); // Pause videos when dragging starts
    this.startPos = this.getPositionX(event);
    this.wrapper.style.transition = 'none';
    this.animationID = requestAnimationFrame(this.animation.bind(this));
  }

  drag(event) {
    if (!this.isDragging) return;
    const currentPosition = this.getPositionX(event);
    const delta = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + delta;
  }

  dragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);

    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = Math.max(60, this.container.offsetWidth * 0.2);

    if (movedBy < -threshold) {
      this.next();
    } else if (movedBy > threshold) {
      this.prev();
    } else {
      this.updatePosition(); // snap back
    }

    if (this.settings.autoplay.delay) this.startAutoplay();
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  animation() {
    if (this.isDragging) {
      this.wrapper.style.transform = `translateX(${this.currentTranslate}px)`;
      requestAnimationFrame(this.animation.bind(this));
    }
  }

  /**
   * When advancing past the last slide in loop mode, append a temporary clone
   * of the first slide, animate to it, then remove the clone and snap back
   * to the real first slide without visual jump.
   */
  loopForwardTransition() {
    // Don't clone while dragging, while another clone is running, or while page is scrolling
    if (this.isCloning || this.isDragging || this.isPageScrolling) return;
    this.isCloning = true;

    const firstSlide = this.slideElements[0];
    if (!firstSlide) {
      this.isCloning = false;
      return;
    }

    const clone = firstSlide.cloneNode(true);
    clone.classList.add('glide-slide-clone');

    // Force cloned images to load immediately to avoid blank frames
    const imgs = clone.querySelectorAll('img');
    imgs.forEach(img => {
      try {
        img.loading = 'eager';
        // ensure src is present (some libs use data-src)
        if (img.dataset && img.dataset.src) img.src = img.dataset.src;
        if (img.dataset && img.dataset.srcset) img.srcset = img.dataset.srcset;
      } catch (e) { /* ignore read-only attributes */ }
    });

    // Handle videos in cloned slides
    const videos = clone.querySelectorAll('video');
    videos.forEach(video => {
      // Ensure cloned videos don't autoplay
      video.autoplay = false;
      video.muted = true; // Ensure muted for autoplay policies
    });

    // Append clone to wrapper so it appears after the last real slide
    this.wrapper.appendChild(clone);

    // Force layout so appended clone is measured
    void this.wrapper.offsetWidth;

    const width = this.container.offsetWidth || 0;
    // Previous visual position is the last real slide
    const prevPos = -(this.totalSlides - 1) * width;
    const target = -this.totalSlides * width;

    // Set internal translate tracking to reflect start of this animation
    this.prevTranslate = prevPos;
    this.currentTranslate = prevPos;

    // Animate to the clone position (use translate3d for smoother GPU compositing)
    this.wrapper.style.transition = 'transform 0.4s ease';
    this.wrapper.style.transform = `translate3d(${target}px, 0, 0)`;

    const onTransitionEnd = (ev) => {
      if (ev.target !== this.wrapper) return;
      if (ev.propertyName && ev.propertyName.indexOf('transform') === -1) return;

      // Cleanup transition listener
      this.wrapper.removeEventListener('transitionend', onTransitionEnd);

      // Remove the temporary clone
      clone.remove();
      this.isCloning = false;

      // Snap to the real first slide without animation
      this.wrapper.style.transition = 'none';
      this.currentIndex = 0;
      const snapTarget = -this.currentIndex * width;
      this.prevTranslate = snapTarget;
      this.currentTranslate = snapTarget;
      this.wrapper.style.transform = `translate3d(${snapTarget}px, 0, 0)`;

      // Allow the browser to paint and then restore transition for future moves
      requestAnimationFrame(() => {
        // small timeout ensures the 'none' transition is applied before clearing
        requestAnimationFrame(() => {
          this.wrapper.style.transition = '';
        });
      });

      // Update UI states (pagination, thumbs, nav)
      this.updatePagination();
      this.updateThumbnailsActive();
      this.updateNavButtons();
    };

    this.wrapper.addEventListener('transitionend', onTransitionEnd);
  }

  /**
   * Smooth backward loop: prepend a clone of the last slide, keep the visual
   * position on the real first slide, then animate right to the clone and
   * snap to the real last slide.
   */
  loopBackwardTransition() {
    // Don't clone while dragging, while another clone is running, or while page is scrolling
    if (this.isCloning || this.isDragging || this.isPageScrolling) return;
    this.isCloning = true;

    const lastSlide = this.slideElements[this.totalSlides - 1];
    if (!lastSlide) {
      this.isCloning = false;
      return;
    }

    const clone = lastSlide.cloneNode(true);
    clone.classList.add('glide-slide-clone');

    // Force cloned images to load immediately to avoid blank frames
    const imgs = clone.querySelectorAll('img');
    imgs.forEach(img => {
      try {
        img.loading = 'eager';
        if (img.dataset && img.dataset.src) img.src = img.dataset.src;
        if (img.dataset && img.dataset.srcset) img.srcset = img.dataset.srcset;
      } catch (e) { /* ignore */ }
    });

    // Handle videos in cloned slides
    const videos = clone.querySelectorAll('video');
    videos.forEach(video => {
      // Ensure cloned videos don't autoplay
      video.autoplay = false;
      video.muted = true; // Ensure muted for autoplay policies
    });

    // Prepend clone to wrapper so it appears before the first real slide
    this.wrapper.insertBefore(clone, this.wrapper.firstChild);

    // Force layout so the DOM update is applied
    void this.wrapper.offsetWidth;

    const width = this.container.offsetWidth || 0;

    // After prepending, the real first slide is at index 1. Start from -width (index 1)
    const startPos = -width;
    const targetPos = 0; // move to cloned slide at 0

    // Ensure internal tracking reflects starting visual position
    this.wrapper.style.transition = 'none';
    this.wrapper.style.transform = `translate3d(${startPos}px, 0, 0)`;
    this.prevTranslate = startPos;
    this.currentTranslate = startPos;

    // Force layout before animating
    void this.wrapper.offsetWidth;

    // Animate to 0 to show the cloned last slide
    this.wrapper.style.transition = 'transform 0.4s ease';
    this.wrapper.style.transform = `translate3d(${targetPos}px, 0, 0)`;

    const onTransitionEnd = (ev) => {
      if (ev.target !== this.wrapper) return;
      if (ev.propertyName && ev.propertyName.indexOf('transform') === -1) return;

      this.wrapper.removeEventListener('transitionend', onTransitionEnd);

      // Remove the temporary clone
      clone.remove();
      this.isCloning = false;

      // Snap to the real last slide without animation
      this.wrapper.style.transition = 'none';
      this.currentIndex = this.totalSlides - 1;
      const snapTarget = -this.currentIndex * width;
      this.prevTranslate = snapTarget;
      this.currentTranslate = snapTarget;
      this.wrapper.style.transform = `translate3d(${snapTarget}px, 0, 0)`;

      // Restore transition for future moves
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.wrapper.style.transition = '';
        });
      });

      // Update UI states (pagination, thumbs, nav)
      this.updatePagination();
      this.updateThumbnailsActive();
      this.updateNavButtons();
    };

    this.wrapper.addEventListener('transitionend', onTransitionEnd);
  }
}